import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, FileSpreadsheet, X, CheckCircle, AlertCircle, Download, Info, XCircle } from 'lucide-react';
import './ItemBulkUpload.css';
import ApiService from '../../../../../INVENTORY/services/apiService';

const ItemBulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isDragging, setIsDragging] = useState(false);
  const [duplicateErrors, setDuplicateErrors] = useState([]);

  // Get token and company from your auth context/localStorage
  const token = localStorage.getItem('token'); // Adjust based on your auth setup
  const company = localStorage.getItem('company'); // Adjust based on your setup

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx')) {
      setMessage({
        type: 'error',
        text: 'Please select a valid Excel file (.xlsx)',
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      setMessage({
        type: 'error',
        text: 'File size exceeds 10MB limit',
      });
      return;
    }

    setFile(selectedFile);
    setMessage({ type: '', text: '' });
    setDuplicateErrors([]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const removeFile = () => {
    setFile(null);
    setUploadProgress(0);
    setMessage({ type: '', text: '' });
    setDuplicateErrors([]);
  };

  const downloadTemplate = () => {
    // Create a link to download the template
    const templateUrl = '/templates/ItemMaster_Template.xlsx'; // Adjust path as needed
    const link = document.createElement('a');
    link.href = templateUrl;
    link.download = 'ItemMaster_Template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Parse duplicate error messages
  const parseDuplicateError = (errorMessage) => {
    const duplicates = [];

    // Pattern 1: SQL UNIQUE KEY constraint violation
    const uniqueKeyPattern = /The duplicate key value is \(([^)]+)\)/gi;
    const uniqueKeyMatches = errorMessage.matchAll(uniqueKeyPattern);

    for (const match of uniqueKeyMatches) {
      duplicates.push({
        type: 'Item Name',
        value: match[1],
      });
    }

    // Pattern 2: Custom duplicate messages
    const customPattern = /duplicate\s+(?:item|key|entry|record)(?:\s+with)?(?:\s+name)?(?:\s+|:\s*)([^\s,\.]+)/gi;
    const customMatches = errorMessage.matchAll(customPattern);

    for (const match of customMatches) {
      if (!duplicates.some((d) => d.value === match[1])) {
        duplicates.push({
          type: 'Item',
          value: match[1],
        });
      }
    }

    // Pattern 3: ItemNo duplicates
    const itemNoPattern = /ItemNo[:\s]+([^\s,\.]+)/gi;
    const itemNoMatches = errorMessage.matchAll(itemNoPattern);

    for (const match of itemNoMatches) {
      if (!duplicates.some((d) => d.value === match[1])) {
        duplicates.push({
          type: 'Item Number',
          value: match[1],
        });
      }
    }

    return duplicates;
  };

  // Format error message for better readability
  const formatErrorMessage = (errorMessage) => {
    // Check if it's a duplicate error
    if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('unique key constraint')) {
      const duplicates = parseDuplicateError(errorMessage);

      if (duplicates.length > 0) {
        setDuplicateErrors(duplicates);
        return `Duplicate items found in your file. Please check the items listed below and remove duplicates.`;
      }

      return 'The file contains duplicate items that already exist in the system. Please review and remove duplicates.';
    }

    // Check for other common errors
    if (errorMessage.toLowerCase().includes('foreign key constraint')) {
      return 'Invalid reference found in your data. Please ensure all referenced items (categories, units, etc.) exist in the system.';
    }

    if (errorMessage.toLowerCase().includes('null') || errorMessage.toLowerCase().includes('required')) {
      return 'Missing required fields in your Excel file. Please ensure all mandatory columns are filled.';
    }

    if (errorMessage.toLowerCase().includes('invalid format') || errorMessage.toLowerCase().includes('data type')) {
      return 'Invalid data format detected. Please check that all values match the expected format in the template.';
    }

    // Return simplified message if it's too long
    if (errorMessage.length > 200) {
      return 'An error occurred while processing your file. Please check your data and try again.';
    }

    return errorMessage;
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({
        type: 'error',
        text: 'Please select a file first',
      });
      return;
    }

    const formData = new FormData();
    formData.append('ExcelFile', file);

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      setDuplicateErrors([]);
      setUploadProgress(0);

      console.log('Starting upload...', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      const apiService = new ApiService();
      const response = await apiService.postMultiPart('CREATE_BULK_ITEMS', formData);

      console.log('Upload response:', response);

      // Check if response indicates an error (statusCode 417, 400, etc.)
      if (response?.data?.statusCode && (response.data.statusCode >= 400 || response.data.statusCode === 417) && !response.data.isSuccessful) {
        // Handle error response that came back as 200 OK but has error payload
        const errorMessage = response.data.message || 'Upload failed. Please try again.';
        const formattedError = formatErrorMessage(errorMessage);

        setMessage({
          type: 'error',
          text: formattedError,
        });
        setUploadProgress(0);
        setLoading(false);
        return;
      }

      // Success case
      if (response?.data?.statusCode === 201 || response?.data?.isSuccessful) {
        setMessage({
          type: 'success',
          text: `Successfully uploaded ${file.name}. Items have been added to the system.`,
        });

        setTimeout(() => {
          navigate('/menu', { replace: true });
        }, 2000);
      } else {
        // Unexpected response format
        setMessage({
          type: 'error',
          text: 'Upload completed but received unexpected response. Please verify your data.',
        });
      }
    } catch (error) {
      console.error('Upload error details:', {
        error,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        message: error.message,
      });

      let errorMessage = 'Upload failed. Please try again.';

      // Handle different error response formats
      if (error.response?.data) {
        const errorData = error.response.data;

        // If error.response.data is a string
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        // If it has a message property
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // If it has an error property
        else if (errorData.error) {
          // Check if error is a string or object
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (typeof errorData.error === 'object') {
            // Try to extract meaningful info from error object
            errorMessage = errorData.error.message || JSON.stringify(errorData.error);
          }
        }
        // If it has errors array
        else if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          errorMessage = errorData.errors.map((err) => (typeof err === 'string' ? err : err.message || JSON.stringify(err))).join(', ');
        }
        // If data is an object, stringify it
        else if (typeof errorData === 'object') {
          try {
            errorMessage = JSON.stringify(errorData, null, 2);
          } catch (e) {
            errorMessage = 'Upload failed. Invalid response from server.';
          }
        }
      }
      // Fallback to error.message
      else if (error.message) {
        errorMessage = error.message;
      }

      // Format the error message for better user experience
      const formattedError = formatErrorMessage(errorMessage);

      setMessage({
        type: 'error',
        text: formattedError,
      });
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="bulk-upload-container">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <div className="bulk-upload-wrapper">
        {/* Header */}
        <div className="upload-header">
          <div className="header-content">
            <FileSpreadsheet className="header-icon" size={32} />
            <div>
              <h1 className="upload-main-title">Item Bulk Upload</h1>
              <p className="upload-subtitle">Upload Excel file to add multiple items at once</p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="info-card">
          <Info size={20} />
          <div className="info-content">
            <h3>Before you upload:</h3>
            <ul>
              <li>Ensure your Excel file follows the correct template format</li>
              <li>Maximum file size: 10MB</li>
              <li>Accepted format: .xlsx</li>
              <li>All required fields must be filled</li>
              <li>Each item must have a unique Item Number and Name</li>
            </ul>
          </div>
        </div>

        {/* Upload Card */}
        <div className="upload-card-modern">
          {/* Drag and Drop Area */}
          <div
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {!file ? (
              <>
                <Upload className="upload-icon" size={48} />
                <h3 className="drop-zone-title">Drag & Drop your Excel file here</h3>
                <p className="drop-zone-text">or</p>
                <label htmlFor="fileInput" className="file-select-btn">
                  Browse Files
                  <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} id="fileInput" className="hidden-input" />
                </label>
                <p className="file-requirements">Supported format: .xlsx (Max size: 10MB)</p>
              </>
            ) : (
              <div className="file-preview">
                <div className="file-info-card">
                  <FileSpreadsheet className="file-icon" size={40} />
                  <div className="file-details">
                    <h4 className="file-name">{file.name}</h4>
                    <p className="file-size">{formatFileSize(file.size)}</p>
                  </div>
                  <button className="remove-file-btn" onClick={removeFile} disabled={loading}>
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {uploadProgress > 0 && (
            <div className="progress-container">
              <div className="progress-info">
                <span>Uploading...</span>
                <span className="progress-percentage">{uploadProgress}%</span>
              </div>
              <div className="progress-bar-modern">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {/* Message Display */}
          {message.text && (
            <div className={`message-box ${message.type}`}>
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
              <span>{message.text}</span>
            </div>
          )}

          {/* Duplicate Errors Display */}
          {duplicateErrors.length > 0 && (
            <div className="duplicate-errors-container">
              <div className="duplicate-errors-header">
                <XCircle size={20} className="duplicate-icon" />
                <h4>Duplicate Items Found ({duplicateErrors.length})</h4>
              </div>
              <div className="duplicate-errors-list">
                {duplicateErrors.map((duplicate, index) => (
                  <div key={index} className="duplicate-error-item">
                    <div className="duplicate-error-badge">{duplicate.type}</div>
                    <div className="duplicate-error-value">{duplicate.value}</div>
                  </div>
                ))}
              </div>
              <div className="duplicate-errors-footer">
                <p>Please remove these duplicate items from your Excel file and try uploading again.</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="btn-secondary" onClick={downloadTemplate} disabled={loading}>
              <Download size={18} />
              Download Template
            </button>
            <button className="btn-primary" onClick={handleUpload} disabled={!file || loading}>
              {loading ? (
                <>
                  <div className="spinner" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Upload File
                </>
              )}
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="instructions-card">
          <h3>Upload Instructions</h3>
          <ol>
            <li>Download the template Excel file using the button above</li>
            <li>Fill in all required item information following the template structure</li>
            <li>Ensure all mandatory fields are completed (ItemNo, ItemDescription, etc.)</li>
            <li>
              <strong>Make sure each item has a unique Item Number and Name</strong> - duplicates will be rejected
            </li>
            <li>Save the file and upload it using the form above</li>
            <li>Wait for the upload to complete and verify the results</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ItemBulkUpload;
